import { Component, inject, input, signal } from '@angular/core';
import type { BackgroundTaskObject } from '@it2go/types';
import {
    BackgroundTaskResultType,
    BackgroundTaskStatus,
} from '@libs/types/src/notifications/enum/background-task.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { ProgressBar } from '@openng/optimus-ui/progressbar';
import { Tag } from '@openng/optimus-ui/tag';
import { ButtonComponent } from '../../../../components/kit/button/button.component';
import { LocalDatePipe } from '../../../shared/pipes/local-date.pipe';
import { BackgroundTaskStore } from '../../store/background-tasks/background-task.store';
import { BackgroundTaskParamsPipe } from '../../store/background-tasks/background-task-params.pipe';

@Component({
    selector: 'app-background-task-item',
    templateUrl: './background-task-item.component.html',
    styleUrl: './background-task-item.component.scss',
    imports: [
        TranslatePipe,
        ProgressBar,
        Tag,
        ButtonComponent,
        LocalDatePipe,
        BackgroundTaskParamsPipe,
    ],
})
export class BackgroundTaskItemComponent {
    private readonly store = inject(BackgroundTaskStore);

    public readonly task = input.required<BackgroundTaskObject>();
    protected readonly opening = signal(false);

    protected async openResult(): Promise<void> {
        this.opening.set(true);
        try {
            await this.store.openResult(this.task());
        } finally {
            this.opening.set(false);
        }
    }

    protected async toggleRead(event: Event): Promise<void> {
        event.stopPropagation();
        const task = this.task();
        await this.store.markRead(task, !task.isRead);
    }

    protected actionLabel(task: BackgroundTaskObject): string {
        return task.resultType === BackgroundTaskResultType.PRINT
            ? 'layout.backgroundTasks.actions.open'
            : 'layout.backgroundTasks.actions.download';
    }

    protected taskIcon(task: BackgroundTaskObject): string {
        if (task.status === BackgroundTaskStatus.COMPLETED) return 'pi-check';
        if (task.status === BackgroundTaskStatus.FAILED) return 'pi-times';
        if (task.status === BackgroundTaskStatus.EXPIRED) return 'pi-clock';
        return task.type.includes('import') ? 'pi-upload' : 'pi-download';
    }

    protected readonly BackgroundTaskStatus = BackgroundTaskStatus;
    protected readonly BackgroundTaskResultType = BackgroundTaskResultType;
    protected readonly taskSeverity: Record<
        BackgroundTaskStatus,
        'secondary' | 'info' | 'success' | 'danger' | 'warn'
    > = {
        [BackgroundTaskStatus.QUEUED]: 'secondary',
        [BackgroundTaskStatus.RUNNING]: 'info',
        [BackgroundTaskStatus.COMPLETED]: 'success',
        [BackgroundTaskStatus.FAILED]: 'danger',
        [BackgroundTaskStatus.EXPIRED]: 'warn',
    };
}
